/**
 * `sessionStorage` access that cannot throw.
 *
 * Storage access throws outright — not returns null — in real
 * configurations: Safari's historical private mode, and any browser set to
 * block site data. Every read and write in the logging subsystem goes
 * through here so a blocked-storage browser degrades to in-memory state for
 * the life of the page rather than breaking the page.
 *
 * `sessionStorage` specifically, never `localStorage`: everything the
 * subsystem persists is tab-scoped by design and must die with the tab.
 *
 * @module logging/core/storage
 */

/** Survives a blocked `sessionStorage`, for the life of the page load. */
const fallback = new Map<string, string>();

export function readStored(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key) ?? fallback.get(key) ?? null;
  } catch {
    return fallback.get(key) ?? null;
  }
}

export function writeStored(key: string, value: string): void {
  fallback.set(key, value);
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Already held in `fallback`; nothing further to do, and nothing worth
    // telling the console about on every event.
  }
}

/**
 * Read a non-negative number, or 0.
 *
 * Corrupt and absent collapse to the same answer deliberately: every
 * persisted number here is an accumulator, and starting one over is a
 * bounded undercount, where trusting a NaN would poison every later sum.
 */
export function readStoredNumber(key: string): number {
  const value = Number(readStored(key));
  return Number.isFinite(value) && value >= 0 ? value : 0;
}
